import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, Observable, of, tap, throwError } from 'rxjs';
import { config } from 'src/app/config/api.config';
import { Activity } from 'src/app/model/Avtivity';
import { CustomResponse } from 'src/app/model/dto/CustomResponse';
import { PresenceDTO } from '../components/models/presence.model';
import { PaginatedResponse } from 'src/app/interfaces/paginateResponse.interface';
import { ActivityResponse } from 'src/app/model/dto/ActivityResponse';
import { InviteMiniJpoDto } from 'src/app/components/mini-jpo-form/inviteMiniJpoDto.model';
import { Countries } from 'src/app/model/countries';
import { Cities } from 'src/app/model/cities';
import {
  ConfirmationInvitation,
  Invitation,
} from 'src/app/components/invite-form/invitation.model';

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  constructor(private http: HttpClient) {}

  private buildPaginationParams(
    page: number = 0,
    size: number = 10,
  ): HttpParams {
    return new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
  }

  public createAvtivity(activity: Activity): Observable<CustomResponse> {
    return this.http.post<CustomResponse>(
      `${config.apiBaseUrl}/activity/saveActivity`,
      activity,
    );
  }

  public allActivity(): Observable<Activity[]> {
    return this.http.get<Activity[]>(`${config.apiBaseUrl}/activity/all`);
  }

  public allActivityPaged(
    page: number = 0,
    size: number = 10,
    search: string = '',
    byType: string = '',
    fromDate: string = '',
    toDate: string = '',
    byCountry: string = '',
  ): Observable<PaginatedResponse<Activity>> {
    let params = this.buildPaginationParams(page, size);
    if (search) {
      params = params.set('search', search);
    }
    if (byType) {
      params = params.set('byType', byType);
    }
    if (fromDate) {
      params = params.set('fromDate', fromDate);
    }
    if (toDate) {
      params = params.set('toDate', toDate);
    }
    if (byCountry) {
      params = params.set('byCountry', byCountry);
    }
    return this.http.get<PaginatedResponse<Activity>>(
      `${config.apiBaseUrl}/activity/all/paged`,
      { params },
    );
  }

  public allForceNActivityByGestionnairePaged(
    iuf_gestionnaire: string,
  ): Observable<ActivityResponse[]> {
    return this.http.get<ActivityResponse[]>(
      `${config.apiBaseUrl}/activity/forcen/` + iuf_gestionnaire,
    );
  }

  public allBeneficiaryActivitiesByIuf(
    iuf: string,
  ): Observable<ActivityResponse[]> {
    return this.http.get<ActivityResponse[]>(
      `${config.apiBaseUrl}/activity/beneficiary/` + iuf,
    );
  }

  // ===== MODIF : confirmation par l'utilisateur connecté (POST, sans IUF) =====
  confirmParticipation(activityId: number): Observable<CustomResponse> {
    return this.http.post<CustomResponse>(
      `${config.apiBaseUrl}/activity/confirmation/${activityId}`,
      {},
    );
  }

  // ===== À AJOUTER dans ActivityService (front) =====
  // imports : import { ConfirmationInvitation, Invitation } from 'src/app/components/invite-form/invitation.model';

  // ===== DÉBUT AJOUT : formulaire d'invitation par jeton (endpoints publics) =====
  getInvitation(token: string): Observable<Invitation> {
    return this.http.get<Invitation>(
      `${config.openBaseUrl}/opencontroller/invitation/${token}`,
    );
  }

  confirmerInvitation(
    token: string,
    demande: ConfirmationInvitation,
  ): Observable<Invitation> {
    return this.http.post<Invitation>(
      `${config.openBaseUrl}/opencontroller/invitation/${token}/confirmation`,
      demande,
    );
  }
  // ===== FIN AJOUT =====

  public updateActivity(activity: Activity, id: string): Observable<any> {
    return this.http.put<Activity>(
      `${config.apiBaseUrl}/activity/update/${id}`,
      activity,
    );
  }
  // ===== DÉBUT AJOUT : modification complète d'une activité =====
  public modifierActivite(
    activity: Activity,
    id: number,
  ): Observable<CustomResponse> {
    return this.http.put<CustomResponse>(
      `${config.apiBaseUrl}/activity/edit/${id}`,
      activity,
    );
  }
  // ===== FIN AJOUT =====

  public deleteActivity(id: number): Observable<CustomResponse> {
    return this.http.delete<CustomResponse>(
      `${config.apiBaseUrl}/activity/delete/${id}`,
    );
  }

  // ===== AJOUT : ce que la suppression emporterait (fenêtre de confirmation) =====
  public impactSuppression(id: number): Observable<CustomResponse> {
    return this.http.get<CustomResponse>(
      `${config.apiBaseUrl}/activity/delete/${id}/impact`,
    ); // ← ton URL
  }

  public listParticipantsActivity(): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/activity/getParticipantsActivity`,
    );
  }

  public listParticipantsPresent(id: number): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/activity/getParticipantPresent/${id}`,
    );
  }

  public listParticipantsPresentFinance(id: number): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/activity/getAllPresentParticipantsFinance/${id}`,
    );
  }

  public getActivityById(activityId: any): Observable<any> {
    return this.http.get<any>(`${config.apiBaseUrl}/activity/${activityId}`);
  }

  public getAllParticipantsOfActivity(activityId: number): Observable<any> {
    return this.http.get(
      `${config.apiBaseUrl}/activity/listparticipants/${activityId}`,
    );
  }

  public getSingleActivityParticipants(activityId: any): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/activity/participants/${activityId}`,
    );
  }

  public setParticipantPresence(
    presenceInfo: PresenceDTO,
  ): Observable<CustomResponse> {
    return this.http.put<CustomResponse>(
      `${config.apiBaseUrl}/activity/presence`,
      presenceInfo,
    );
  }

  public getConfirmationStatus(
    iuf: string,
    id?: number,
  ): Observable<CustomResponse> {
    return this.http.get<CustomResponse>(
      `${config.apiBaseUrl}/activity/checkConfirmation/${id}/${iuf}`,
    );
  }

  public getListActivities(): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/activity/allActivitiesOderByDate`,
    );
  }

  public uploadInviteexcelFile(
    file: File,
    activityId: number,
  ): Observable<CustomResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<CustomResponse>(
      `${config.apiBaseUrl}/invite/addinvitewithexcelfile/${activityId}`,
      formData,
    );
  }
  public exportPdfListFinance(activityId: number): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/alfresco/renditions/content/activities/${activityId}`,
    );
  }
  // ===== AJOUT : contenu du PDF Alfresco, pour l'afficher dans la page =====
  public contenuRenditionPdf(idNoeud: string): Observable<Blob> {
    return this.http.get(
      `${config.openBaseUrl}/opencontroller/renditions/content/${idNoeud}`,
      { responseType: 'blob' },
    );
  }
  public getCurrentJpo(activityType: string): Observable<CustomResponse> {
    return this.http.get<CustomResponse>(
      `${config.openBaseUrl}/opencontroller/activity/current/${activityType}`,
    );
  }
  public getActivityByIdV2(activityId: number): Observable<CustomResponse> {
    return this.http.get<CustomResponse>(
      `${config.openBaseUrl}/opencontroller/activity/${activityId}/details`,
    );
  }
  public jpoSubmission(
    dto: InviteMiniJpoDto,
    activityId: number,
  ): Observable<CustomResponse> {
    return this.http.post<CustomResponse>(
      `${config.openBaseUrl}/opencontroller/activity/mini-jpo/submission/${activityId}`,
      dto,
    );
  }

  public commercantLivreurSubmission(
    dto: any,
    activityId: number,
  ): Observable<CustomResponse> {
    return this.http.post<CustomResponse>(
      `${config.openBaseUrl}/opencontroller/activity/commercant-livreur/submission/${activityId}`,
      dto,
    );
  }

  public generateActivityQRCode(idActivity: number): Observable<any> {
    return this.http.get<Blob>(
      `${config.apiBaseUrl}/qrcode/generate/${idActivity}`,
      {
        responseType: 'blob' as 'json',
      },
    );
  }

  getActivitiesByRange(
    iuf: string,
    start: string,
    end: string,
  ): Observable<ActivityResponse[]> {
    let params = new HttpParams();
    params = params.append('start', start);
    params = params.append('end', end);

    return this.http.get<ActivityResponse[]>(
      `${config.apiBaseUrl}/activity/beneficiary/${iuf}`,
      { params: params },
    );
  }

  removeParticipant(activityId: number, id: string): Observable<any> {
    return this.http.delete(
      `${config.apiBaseUrl}/activity/${activityId}/participant/${id}`,
    );
  }

  updateParticipantPhone(
    activityId: number,
    id: string,
    telephone: string,
  ): Observable<any> {
    return this.http.patch(
      `${config.apiBaseUrl}/activity/${activityId}/participant/phone`,
      {
        id,
        telephone,
      },
    );
  }

  public getAllCohortesParcours(): Observable<any> {
    return this.http.get<any[]>(`${config.apiBaseUrl}/cohorteParcours/all`);
  }

  public checkTelephoneExists(telephone: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${config.openBaseUrl}/opencontroller/commercant-livreur?telephone=${telephone}`,
    );
  }
  // ===== AJOUT : contrôle du numéro Wave, version authentifiée (écran des confirmations) =====
  public verifierNumeroWave(telephone: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${config.apiBaseUrl}/activity/telephone-beneficiaire`,
      { params: { telephone } }, // Angular encode le numéro dans l'URL
    );
  }

  getCountries(): Observable<Countries[]> {
    return this.http.get<Countries[]>(
      `${config.openBaseUrl}/opencontroller/countries`,
    );
  }
  public listCity(): Observable<Cities[]> {
    return this.http.get<Cities[]>(`${config.apiBaseUrl}/cities/All`);
  }

  public cityListbyIsoCode(isoCode: string): Observable<any> {
    return this.http.get<any>(
      `${config.apiBaseUrl}/cities/country-code/` + isoCode,
    );
  }

  getAllForceNManagers(): Observable<any> {
    return this.http.get<any>(`${config.apiBaseUrl}/usermanager/forcen-only`);
  }

  getPresenceCode(activityId: number): Observable<CustomResponse> {
    return this.http.get<CustomResponse>(
      `${config.apiBaseUrl}/activity/${activityId}/presence-code`,
    );
  }

  regeneratePresenceCode(activityId: number): Observable<CustomResponse> {
    return this.http.post<CustomResponse>(
      `${config.apiBaseUrl}/activity/${activityId}/presence-code/regenerate`,
      {},
    );
  }
}
